import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {FreightServiceState} from "@/interfaces/state/freightservice";
import {FreightService} from "@/interfaces/entity/freightservice";
import {
    createFreightService, deleteFreightService,
    listFreightService, listFreightServiceCustomer,
    singleFreightService,
    updateFreightService
} from "@/modules/freightservices/services";
import {IdMapper} from "@/interfaces/state/base";
import {PaginationMeta} from "@/interfaces/common";
import {FreightServiceParams} from "@/modules/freightservices/params";

@Module({ dynamic: true, name: "freightService", store })
class FreightServiceModule extends VuexModule implements FreightServiceState {
    data: IdMapper<FreightService> = {};
    ids: string[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: FreightServiceParams = new FreightServiceParams();

    get freightServices() {
        return this.ids.map((id: string) => this.data[id]);
    }

    get freightServiceDefault() {
        return Object.values(this.data).find((item: FreightService) => item.serviceCode.includes('CPN'));
    }

    @Mutation
    private UPDATE_FREIGHT_SERVICE_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private MAP_FREIGHTSERVICE(payload: any) {
        const idMapper: IdMapper<FreightService> = {};
        this.meta = payload.meta;
        this.ids = payload.datas?.map((service: FreightService) => {
            idMapper[service.rowGuid!] = {...service, isDeleting: false};
            return service.rowGuid;
        });
        this.data = idMapper;
    }

    @Mutation
    private ADD_FREIGHTSERVICE(payload: FreightService) {
        if (!payload || this.ids.includes(payload.rowGuid) || this.data[payload.rowGuid]) return;
        this.ids = [ ...this.ids, payload.rowGuid ];
        this.data = { ...this.data, [payload.rowGuid]: {...payload, isDeleting: false} }
    }

    @Mutation
    private UPDATE_FREIGHTSERVICE(payload: FreightService) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.data[payload.rowGuid] = { ...payload, isDeleting: false }
    }

    @Mutation
    private DELETE_FREIGHTSERVICE(payload: FreightService) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.ids = this.ids.filter((id: string) => id !== payload.rowGuid);
        delete this.data[payload.rowGuid];
    }

    @Mutation
    private START_DELETE_FREIGHTSERVICE(id: string) {
        this.data[id].isDeleting = true;
    }

    @Mutation
    private END_DELETE_FREIGHTSERVICE(id: string) {
        this.data[id].isDeleting = false;
    }

    @Action({ rawError: true })
    public addFreightService(payload: FreightService) {
        this.ADD_FREIGHTSERVICE(payload);
    }

    @Action({ rawError: true })
    public async createFreightService(form: any) {
        const { data } = await createFreightService(form);
        this.ADD_FREIGHTSERVICE(data.data);
    }

    @Action({ rawError: true })
    public async listFreightService(params: FreightServiceParams = new FreightServiceParams()) {
        const { data } = await listFreightService(params);
        this.MAP_FREIGHTSERVICE(data);
        return data.datas;
    }

    @Action({ rawError: true })
    public async singleFreightService(id: string) {
        const { data } = await singleFreightService(id);
        return data.data;
    }

    @Action({ rawError: true })
    public async updateFreightService(dataReq: any) {
        const { data } = await updateFreightService(dataReq.rowGuid, dataReq);
        this.UPDATE_FREIGHTSERVICE(data.data);
    }

    @Action({ rawError: true })
    public async deleteFreightService(id: string) {
        this.START_DELETE_FREIGHTSERVICE(id);
        const { data } = await deleteFreightService(id);
        this.END_DELETE_FREIGHTSERVICE(id);
        this.DELETE_FREIGHTSERVICE(data.data);
    }

    @Action({ rawError: true })
    public updateFreightServiceParams(payload: any) {
        this.UPDATE_FREIGHT_SERVICE_PARAMS(payload);
    }

    @Action({ rawError: true })
    public async listFreightServiceOfCustomer(payload: any) {
        const { data } = await listFreightServiceCustomer(payload);
        this.MAP_FREIGHTSERVICE(data);
        return data.datas;
    }
}

export const FreightServiceStore = getModule(FreightServiceModule);